import { createAuth0 } from "@auth0/auth0-vue";
import { createGtm } from "@gtm-support/vue-gtm";
import * as Sentry from "@sentry/vue";
import { defineRule } from "vee-validate";
import * as Vue from "vue";
import { Tab, Tabs } from "vue3-tabs-component";
import Vuex from "vuex";
import App from "./App.vue";
import "./registerServiceWorker";
import router from "./router";
import store from "./state";

const app = Vue.createApp(App).component("tabs", Tabs).component("tab", Tab);
const params = new URLSearchParams(document.location.search);
const redirect = params.get("redirect");

if (redirect) {
  console.log(`Redirecting to ${redirect}...`);
  localStorage.setItem("redirect", redirect);
}

Sentry.init({
  app,
  dsn: process.env.VUE_APP_SENTRY_DSN,
  // Sentry v8+ merged @sentry/tracing into @sentry/vue. BrowserTracing is now
  // browserTracingIntegration({ router }) (replacing vueRouterInstrumentation),
  // tracingOrigins moved to top-level tracePropagationTargets, and
  // trackComponents/hooks moved under tracingOptions. logErrors was removed —
  // the Vue error handler is attached by default (attachErrorHandler).
  integrations: [Sentry.browserTracingIntegration({ router })],
  environment: process.env.NODE_ENV,
  tracesSampleRate: 1.0,
  tracePropagationTargets: ["*"],
  tracingOptions: {
    trackComponents: true,
    hooks: ["activate", "create", "mount", "unmount", "update"],
  },
});

defineRule("alphaExt", (value) => {
  if (
    value &&
    !/^[A-Za-z\u00c0-\u00ff}][A-Za-z\u00c0-\u00ff'-]+([ A-Za-z\u00c0-\u00ff][A-Za-z\u00c0-\u00ff'-]+)*/u.test(
      value,
    )
  ) {
    return "This field cannot contain special characters.";
  }

  return true;
});

defineRule("email", (value) => {
  if (!value || !value.length) {
    return true;
  }
  if (!/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}/.test(value)) {
    return "This field must be a valid email.";
  }
  return true;
});

defineRule("minMax", (value, [min, max]) => {
  if (!value || !value.length) {
    return true;
  }
  const numericValue = Number(value.length);
  if (numericValue < min) {
    return `This field must contain more than ${min} characters`;
  }
  if (numericValue > max) {
    return `This field must contain less than ${max} characters`;
  }
  return true;
});

defineRule("required", (value) => {
  if (!value || !value.length) {
    return "This field is required.";
  }
  return true;
});

app
  .use(
    createAuth0({
      domain: process.env.VUE_APP_AUTH0_DOMAIN,
      clientId: process.env.VUE_APP_AUTH0_CLIENTID,
      authorizationParams: {
        redirect_uri: window.location.origin,
        // Note: This will not work on localhost in Safari due to ITP.
        audience: process.env.VUE_APP_AUTH0_AUDIENCE,
      },
    }),
  )
  .use(store)
  .use(router)
  .use(Vuex)
  .use(
    createGtm({
      id: process.env.VUE_APP_GTM_ID,
      defer: false,
      compatibility: true,
      debug: process.env.NODE_ENV !== "production",
      vueRouter: router,
      trackOnNextTick: true,
    }),
  )
  .mount("#app");
